import {
  buildJsonFiles,
  deleteAndMigrateHistoricalDataLayouts,
  deleteHistoricalLayoutFile,
  downloadHistoricalDataLayoutFile,
  getHistoricalDataFilesStatus,
  HistoricalDataFileResponse,
  HistoricalDataFilesStatusResponse,
  uploadHistoricalDataFile,
  validateHistoricalDataLayoutFiles,
} from "api/generated";
import dayjs from "dayjs";
import { useClient } from "hooks/useClient";
import { useCallback, useMemo } from "react";
import {
  RcFile,
  UploadRequestError,
  UploadRequestOption,
} from "rc-upload/lib/interface";
import { message } from "antd";

export enum UPLOAD_KIND {
  PLANTAS = "PLANTAS",
  ESTACIONES = "ESTACIONES",
}

export enum HISTORICAL_DATA_TYPE {
  VENTAS = "VENTAS",
  TRASPASOS_ENTRADA = "TRASPASOS_ENTRADA",
  INVENTARIOS = "INVENTARIOS",
  COMPRAS = "COMPRAS",
  TRASPASOS_SALIDA = "TRASPASOS_SALIDA",
  PRE_VALIDATION_SUMMARY = "PRE_VALIDATION_SUMMARY",
  DIFFERENCES_FILE = "DIFFERENCES_FILE",
  JSONS_SUMMARY = "JSONS_SUMMARY",
  JSONS_ZIP = "JSONS_ZIP",
}

export interface FileRule {
  label: string;
  type: HISTORICAL_DATA_TYPE;
  description: string;
  requiredFor: UPLOAD_KIND[];
}

interface ValidationError {
  type: HISTORICAL_DATA_TYPE;
  message: string;
  severity: "error" | "warning";
}

/**
 * Reglas de archivos para la carga de datos históricos
 */
export const FILE_RULES: FileRule[] = [
  {
    label: "Traspasos de Entrada",
    type: HISTORICAL_DATA_TYPE.TRASPASOS_ENTRADA,
    description: "Archivo de entradas de combustible",
    requiredFor: [UPLOAD_KIND.PLANTAS, UPLOAD_KIND.ESTACIONES],
  },
  {
    label: "Ventas",
    type: HISTORICAL_DATA_TYPE.VENTAS,
    description: "Archivo de salidas (ingresos) de combustible",
    requiredFor: [UPLOAD_KIND.PLANTAS, UPLOAD_KIND.ESTACIONES],
  },
  {
    label: "Inventarios",
    type: HISTORICAL_DATA_TYPE.INVENTARIOS,
    description: "Archivo de mediciones de nivel de tanques",
    requiredFor: [UPLOAD_KIND.PLANTAS, UPLOAD_KIND.ESTACIONES],
  },
  {
    label: "Compras",
    type: HISTORICAL_DATA_TYPE.COMPRAS,
    description: "Archivo de compras (recepciones) de combustible",
    requiredFor: [UPLOAD_KIND.PLANTAS],
  },
  {
    label: "Traspasos de Salida",
    type: HISTORICAL_DATA_TYPE.TRASPASOS_SALIDA,
    description: "Archivo de traspasos de salidas de combustible",
    requiredFor: [UPLOAD_KIND.PLANTAS],
  },
];

/**
 * Tipos de puntos de distribución con sus descripciones
 */
export const DISTRIBUTION_POINT_KINDS = [
  {
    label: "Plantas",
    key: UPLOAD_KIND.PLANTAS,
    description: "Plantas de almacenamiento con 5 tipos de archivos requeridos",
  },
  {
    label: "Estaciones",
    key: UPLOAD_KIND.ESTACIONES,
    description: "Estaciones de carburación con 3 tipos de archivos requeridos",
  },
];

export const useHistoricalData = () => {
  const { client } = useClient();

  /**
   * Mapea los tipos del backend a los tipos del frontend
   * @param backendType Tipo de dato proveniente del backend
   * @returns Tipo de dato del frontend (HISTORICAL_DATA_TYPE)
   */
  const mapBackendTypeToFrontend = useCallback(
    (backendType: string): HISTORICAL_DATA_TYPE => {
      switch (backendType) {
        case "VENTAS":
          return HISTORICAL_DATA_TYPE.VENTAS;
        case "TRASPASOS_ENTRADA":
          return HISTORICAL_DATA_TYPE.TRASPASOS_ENTRADA;
        case "INVENTARIOS":
          return HISTORICAL_DATA_TYPE.INVENTARIOS;
        case "COMPRAS":
          return HISTORICAL_DATA_TYPE.COMPRAS;
        case "TRASPASOS_SALIDA":
          return HISTORICAL_DATA_TYPE.TRASPASOS_SALIDA;
        case "PRE_VALIDATION_SUMMARY":
          return HISTORICAL_DATA_TYPE.PRE_VALIDATION_SUMMARY;
        case "DIFFERENCES_FILE":
          return HISTORICAL_DATA_TYPE.DIFFERENCES_FILE;
        case "JSONS_SUMMARY":
          return HISTORICAL_DATA_TYPE.JSONS_SUMMARY;
        case "JSONS_ZIP":
          return HISTORICAL_DATA_TYPE.JSONS_ZIP;
        default:
          throw new Error(`Tipo desconocido: ${backendType}`);
      }
    },
    []
  );

  /**
   * Sube un archivo de datos históricos al servidor
   * @param fileContent Contenido del archivo a subir
   * @param label Tipo de archivo (HISTORICAL_DATA_TYPE)
   * @param year Año del período
   * @param month Mes del período
   * @param kind Tipo de punto de distribución (UPLOAD_KIND)
   * @returns Promesa con la respuesta del servidor o undefined
   */
  const uploadFile = useCallback(
    async (
      fileContent: string,
      label: HISTORICAL_DATA_TYPE,
      year: string,
      month: string,
      kind: UPLOAD_KIND
    ): Promise<HistoricalDataFilesStatusResponse | undefined> => {
      const blob = new Blob([fileContent], { type: "text/csv" });
      return uploadHistoricalDataFile({
        client,
        body: {
          file: blob,
        },
        query: {
          type: label,
          year,
          month,
          kind,
        },
      }).then(({ data }) => {
        message.success(
          `El archivo de ${label} fue enviado al servidor correctamente`
        );
        return data;
      });
    },
    [client]
  );

  /**
   * Verifica el estado de los archivos para un período específico
   * @param kind Tipo de punto de distribución (UPLOAD_KIND)
   * @param year Año del período
   * @param month Mes del período
   * @returns Promesa con los archivos existentes y logs de validación
   */
  const checkFileStatus = useCallback(
    async (
      kind: UPLOAD_KIND,
      year: string,
      month: string
    ): Promise<HistoricalDataFilesStatusResponse | undefined> => {
      const { data } = await getHistoricalDataFilesStatus({
        client,
        query: { kind, year, month },
      });
      return data;
    },
    [client]
  );

  /**
   * Ejecuta la validación de archivos para un período específico
   * @param kind Tipo de punto de distribución (UPLOAD_KIND)
   * @param year Año del período
   * @param month Mes del período
   * @returns Promesa con los archivos existentes y logs de validación
   */
  const validateLayoutFiles = useCallback(
    async (
      kind: UPLOAD_KIND,
      year: string,
      month: string
    ): Promise<HistoricalDataFilesStatusResponse | undefined> => {
      const { data } = await validateHistoricalDataLayoutFiles({
        client,
        body: {
          kind,
          month,
          year,
        },
      });

      return data;
    },
    [client]
  );

  /**
   * Descarga un archivo de layout para un tipo específico
   * @param label Tipo de archivo (HISTORICAL_DATA_TYPE)
   * @param year Año del período
   * @param month Mes del período
   * @param kind Tipo de punto de distribución (UPLOAD_KIND)
   * @returns Promesa que se resuelve cuando la descarga se completa
   */
  const downloadLayoutFile = useCallback(
    async (
      label: HISTORICAL_DATA_TYPE,
      year: string,
      month: string,
      kind: UPLOAD_KIND
    ) => {
      const { data } = await downloadHistoricalDataLayoutFile({
        client,
        query: { type: label, year, month, kind },
      });

      if (data) {
        const url = window.URL.createObjectURL(data as unknown as Blob);
        const link = document.createElement("a");
        link.href = url;

        switch (label) {
          case HISTORICAL_DATA_TYPE.PRE_VALIDATION_SUMMARY:
            link.setAttribute(
              "download",
              `resumen_por_pdd_${kind}_${year}_${month}.csv`
            );
            break;
          case HISTORICAL_DATA_TYPE.DIFFERENCES_FILE:
            link.setAttribute(
              "download",
              `layouts_vs_database_differences_${kind}_${year}_${month}.csv`
            );
            break;
          case HISTORICAL_DATA_TYPE.JSONS_SUMMARY:
            link.setAttribute(
              "download",
              `json_summary_${kind}_${year}_${month}.csv`
            );
            break;
          case HISTORICAL_DATA_TYPE.JSONS_ZIP:
            link.setAttribute(
              "download",
              `jsons_zip_${kind}_${year}_${month}.zip`
            );
            break;

          default:
            link.setAttribute("download", `layout_${label.toLowerCase()}.csv`);
            break;
        }

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    [client]
  );

  const downloadHistoricalDataFile = useCallback(
    async (
      kind: UPLOAD_KIND,
      year: string,
      month: string,
      type: HISTORICAL_DATA_TYPE
    ) => {
      try {
        const { data } = await downloadHistoricalDataLayoutFile({
          client,
          query: {
            type,
            year,
            month,
            kind,
          },
        });
        return data;
      } catch (error) {
        throw new Error(`Error al descargar el resumen: ${error}`);
      }
    },
    [client]
  );

  /**
   * Determina si todos los archivos requeridos han sido subidos
   * @param existingFiles Archivos existentes en el servidor
   * @param kindSelected Tipo de punto de distribución seleccionado
   * @returns true si todos los archivos requeridos están presentes
   */
  const allFilesUploaded = useMemo(
    () =>
      (
        existingFiles: HistoricalDataFileResponse[],
        kindSelected: UPLOAD_KIND
      ) => {
        const requiredFiles =
          kindSelected === UPLOAD_KIND.PLANTAS
            ? [
                HISTORICAL_DATA_TYPE.VENTAS,
                //HISTORICAL_DATA_TYPE.TRASPASOS_ENTRADA, // Opcional
                HISTORICAL_DATA_TYPE.INVENTARIOS,
                HISTORICAL_DATA_TYPE.COMPRAS,
                HISTORICAL_DATA_TYPE.TRASPASOS_SALIDA,
              ]
            : [
                HISTORICAL_DATA_TYPE.VENTAS,
                HISTORICAL_DATA_TYPE.TRASPASOS_ENTRADA,
                HISTORICAL_DATA_TYPE.INVENTARIOS,
              ];

        return requiredFiles.every((fileType) =>
          existingFiles.map((item) => item.type).includes(fileType)
        );
      },
    []
  );

  /**
   * Formatea una fecha dayjs a un string de año localizado
   * @param date Fecha dayjs
   * @returns String con el año formateado
   */
  const formatLocaleYear = useCallback((date: dayjs.Dayjs): string => {
    return date.locale("es-mx").format("YYYY").toUpperCase();
  }, []);

  /**
   * Formatea una fecha dayjs a un string de mes localizado
   * @param date Fecha dayjs
   * @returns String con el mes formateado
   */
  const formatLocaleMonth = useCallback((date: dayjs.Dayjs): string => {
    return date.locale("es-mx").format("MMMM").toUpperCase();
  }, []);

  /**
   * Transforma un archivo para subirlo al servidor
   */
  const transformFileForUpload = useCallback(
    async (
      options: UploadRequestOption,
      type: HISTORICAL_DATA_TYPE,
      onUpload: (
        fileContent: string,
        label: HISTORICAL_DATA_TYPE
      ) => Promise<HistoricalDataFilesStatusResponse | undefined>
    ) => {
      const { file, onSuccess, onError } = options;
      const reader = new FileReader();

      return new Promise<void>((resolve, reject) => {
        reader.onload = async (event) => {
          try {
            const content = event.target?.result as string;
            await onUpload(content, type);
            onSuccess?.({ success: true });
            resolve();
          } catch (err) {
            const uploadError =
              err instanceof Error ? err : new Error(String(err));
            onError?.(uploadError as UploadRequestError);
            message.error(`Error al subir el archivo de ${type} al servidor`);
            reject(err);
          }
        };

        reader.onerror = (error) => {
          onError?.(error);
          message.error(`El archivo de ${type} no se pudo leer`);
          reject(error);
        };

        reader.readAsText(file as Blob);
      });
    },
    []
  );

  /**
   * Valida el tipo de archivo antes de subirlo
   */
  const validateFileBeforeUpload = useCallback((file: RcFile): boolean => {
    const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");
    if (!isCSV) {
      message.error("Solo se pueden cargar archivos CSV");
    }
    return isCSV;
  }, []);

  /**
   * Obtiene los archivos requeridos para un tipo de punto de distribución
   */
  const getRequiredFiles = useCallback(
    (kind: UPLOAD_KIND): HISTORICAL_DATA_TYPE[] => {
      return FILE_RULES.filter((rule) => rule.requiredFor.includes(kind)).map(
        (rule) => rule.type
      );
    },
    []
  );

  /**
   * Formatea una fecha dayjs a un string de mes y año
   */
  const formatMonthYear = useCallback((date?: dayjs.Dayjs): string => {
    return date
      ? date.locale("es-mx").format("MMMM YYYY").toUpperCase()
      : "Sin seleccionar";
  }, []);

  /**
   * Formatea el tamaño de un archivo en bytes a una cadena legible
   * @param bytes Tamaño del archivo en bytes
   * @returns Cadena formateada (ej. "1.23 MB")
   */
  const formatFileSize = useCallback((bytes?: number): string => {
    if (!bytes || bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }, []);

  /**
   * Obtiene el tamaño de un archivo específico de la lista de archivos actuales
   * @param currentFiles Lista de archivos actuales
   * @param type Tipo de archivo a buscar
   * @returns Tamaño formateado del archivo o cadena vacía si no se encuentra
   */
  const getFileSize = useCallback(
    (
      currentFiles: HistoricalDataFileResponse[],
      type: HISTORICAL_DATA_TYPE
    ): string => {
      const file = currentFiles.find((item) => item.type === type);
      return formatFileSize(file?.fileSize);
    },
    [formatFileSize]
  );

  /**
   * Procesa los logs de validación y los convierte en un formato para Timeline
   * @param validationLogs Cadena con los logs de validación
   * @returns Array de items para el componente Timeline
   */
  const processValidationLogs = useCallback((validationLogs: string) => {
    return validationLogs
      .split("\n")
      .filter((line) => line.trim() !== "")
      .map((line, index) => ({
        key: index,
        children: line,
        color: line.toLowerCase().includes("error")
          ? "red"
          : line.toLowerCase().includes("warning")
          ? "orange"
          : "green",
      }));
  }, []);

  /**
   * Extrae las validaciones por tipo de archivo de los logs
   * @param validationLogs Cadena con los logs de validación
   * @param requiredFiles Tipos de archivos requeridos
   * @returns Objeto con validaciones organizadas por tipo de archivo
   */
  const getFileValidations = useCallback(
    (
      validationLogs: string,
      requiredFiles: { type: HISTORICAL_DATA_TYPE }[]
    ): Record<HISTORICAL_DATA_TYPE, ValidationError[]> => {
      const validations: Record<HISTORICAL_DATA_TYPE, ValidationError[]> =
        {} as never;

      requiredFiles.forEach((file) => {
        validations[file.type] = [];
      });

      validationLogs.split("\n").forEach((line) => {
        if (!line.trim()) return;

        const typeMatch =
          line.match(/❌\s*\[([^\]]+)/) || line.match(/⚠️\s*\[([^\]]+)/);
        if (!typeMatch) return;

        let typeString = typeMatch[1].trim().toUpperCase();
        const severity = line.includes("❌") ? "error" : "warning";

        // Normalizar "TRASPASOS" a "TRASPASOS_ENTRADA"
        if (typeString === "TRASPASOS") {
          typeString = "TRASPASOS_ENTRADA";
        }

        let foundType: HISTORICAL_DATA_TYPE | null = null;
        for (const type of Object.values(HISTORICAL_DATA_TYPE)) {
          if (typeString.includes(type)) {
            foundType = type;
            break;
          }
        }

        if (foundType && validations[foundType]) {
          validations[foundType].push({
            type: foundType,
            message: line.replace(/^.*?\]\s*/, "").trim(),
            severity,
          });
        }
      });

      return validations;
    },
    []
  );

  /**
   * Determina el estado de validación de un archivo
   * @param currentFiles Lista de archivos actuales
   * @param fileValidations Validaciones por tipo de archivo
   * @param type Tipo de archivo a evaluar
   * @returns Estado de validación ('missing' | 'error' | 'warning' | 'valid')
   */
  const getFileStatus = useCallback(
    (
      currentFiles: HistoricalDataFileResponse[],
      fileValidations: Record<HISTORICAL_DATA_TYPE, ValidationError[]>,
      type: HISTORICAL_DATA_TYPE
    ) => {
      const file = currentFiles.find((item) => item.type === type);
      if (!file) return "missing";

      const validations = fileValidations[type] || [];
      if (validations.some((v) => v.severity === "error")) return "error";
      if (validations.some((v) => v.severity === "warning")) return "warning";
      return "valid";
    },
    []
  );

  /**
   * Ejecuta la migracion de archivos para un período específico
   * @param kind Tipo de punto de distribución (UPLOAD_KIND)
   * @param year Año del período
   * @param month Mes del período
   * @param kill Determina si la base de datos sera eliminada
   * @returns Promesa con los archivos existentes y logs de eliminacion
   */
  const deleteAndMigrate = useCallback(
    async (
      kind: UPLOAD_KIND,
      year: string,
      month: string,
      kill: boolean
    ): Promise<HistoricalDataFilesStatusResponse | undefined> => {
      const { data } = await deleteAndMigrateHistoricalDataLayouts({
        client,
        query: {
          kind,
          month,
          year,
          kill,
        },
      });

      return data;
    },
    [client]
  );

  /**
   * Elimina un archivo de datos históricos al servidor
   * @param label Tipo de archivo (HISTORICAL_DATA_TYPE)
   * @param year Año del período
   * @param month Mes del período
   * @param kind Tipo de punto de distribución (UPLOAD_KIND)
   * @returns Promesa con la respuesta del servidor o undefined
   */
  const deleteFile = useCallback(
    async (
      label: HISTORICAL_DATA_TYPE,
      year: string,
      month: string,
      kind: UPLOAD_KIND
    ): Promise<HistoricalDataFilesStatusResponse | undefined> => {
      return deleteHistoricalLayoutFile({
        client,
        query: {
          type: label,
          year,
          month,
          kind,
        },
      }).then(({ data }) => data);
    },
    [client]
  );

  /**
   * Ejecuta la creacion de archivos json para un período específico
   * @param kind Tipo de punto de distribución (UPLOAD_KIND)
   * @param year Año del período
   * @param month Mes del período
   * @returns Promesa con los archivos existentes y logs relacionados
   */
  const createJsonFiles = useCallback(
    async (
      kind: UPLOAD_KIND,
      year: string,
      month: string
    ): Promise<HistoricalDataFilesStatusResponse | undefined> => {
      const { data } = await buildJsonFiles({
        client,
        body: {
          kind,
          month,
          year,
          backendPath: client.getConfig().baseURL,
        },
      });

      return data;
    },
    [client]
  );

  return {
    mapBackendTypeToFrontend,
    uploadFile,
    checkFileStatus,
    downloadLayoutFile,
    allFilesUploaded,
    formatLocaleYear,
    formatLocaleMonth,
    transformFileForUpload,
    validateFileBeforeUpload,
    validateLayoutFiles,
    getRequiredFiles,
    formatMonthYear,
    FILE_RULES,
    DISTRIBUTION_POINT_KINDS,
    formatFileSize,
    getFileSize,
    processValidationLogs,
    getFileValidations,
    getFileStatus,
    downloadHistoricalDataFile,
    deleteAndMigrate,
    deleteFile,
    createJsonFiles,
  };
};
